import MVPD from '@/components/MVPD/MVPD'
import ActivateDevice from '@/components/auth/ActivateDevice'
import { MultiStepItem, MultiStepProgressIndicator } from '@/components/auth/MultiStepProgressIndicator'
import { VerifyCodeResult } from '@/components/types'
import getRootUrl from '@/hooks/getRootUrl'
import { GrandstandLogo } from '@grandstand-web/bally-web-shared/src/components/GrandstandLogo'
import { useLocalStorage } from '@grandstand-web/bally-web-shared/src/hooks/useLocalStorage'
import { ApiClient } from '@grandstand-web/bally-web-shared/src/newPackages/ApiClient'
import {
  ConfigServiceContext,
  useContextUnconditionally,
} from '@grandstand-web/bally-web-shared/src/services/config/ConfigService'
import { ManualRefreshType } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import Link from 'next/link'
import { NextRouter, useRouter } from 'next/router'
import { useReducer } from 'react'
import styled from 'styled-components'

const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  position: relative;
`
const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px;
  top: 0;
  left: 0;
`
const FormContainer = styled.div`
  width: 528px;
  padding: 10px 20px;
  margin-top: 30px;
  border-radius: 8px;
  background-color: var(--surface);
  color: var(--on-light);
  @media screen and (max-width: 580px) {
    width: 80%;
  }
`

interface State {
  isCodeActivated: boolean
  activationCode: string
}

interface SetIsCodeActivatedAction {
  type: 'SET_IS_CODE_ACTIVATED'
  payload: boolean
}

interface SetActivationCodeAction {
  type: 'SET_ACTIVATION_CODE'
  payload: string
}

type Action = SetIsCodeActivatedAction | SetActivationCodeAction

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_IS_CODE_ACTIVATED':
      return { ...state, isCodeActivated: action.payload }
    case 'SET_ACTIVATION_CODE':
      return { ...state, activationCode: action.payload }
    default:
      return state
  }
}

const getInitialState = (router: NextRouter): State => {
  const activationCode = (router.query?.code ?? '') as string
  return {
    isCodeActivated: activationCode.length > 0,
    activationCode,
  }
}

const ActivatePage = () => {
  const router = useRouter()
  const [state, dispatch] = useReducer(reducer, getInitialState(router))
  const { isCodeActivated, activationCode } = state
  const { currentConfig } = useContextUnconditionally(ConfigServiceContext)
  const [_redirectUrl, setRedirectUrl] = useLocalStorage<string | null>('activatedRedirectUrl', null)
  const [_manualRefreshType, setManualRefreshType] = useLocalStorage<ManualRefreshType>('manualRefreshType', 'none')

  const handleProviderSelected = async (providerId: string) => {
    setManualRefreshType('logInMVPD')
    setRedirectUrl((router.query?.redirect_url as string) ?? null)

    try {
      let baseUrl = currentConfig.API.services.auth_services.mvpd.provider_auth
      if (!baseUrl || baseUrl.length === 0) {
        throw new Error('Invalid config for verifyCode')
      }
      const redirectUri = `${getRootUrl()}activated/mvpd/device`

      const url = new URL(baseUrl)
      url.searchParams.set('code', activationCode)
      url.searchParams.set('provider', providerId)
      url.searchParams.set('redirect_uri', redirectUri.toString())

      const { loginUrl } = await ApiClient.convenientApiFetch({
        method: 'GET',
        url: url.toString(),
      }).then((d) => d.json())

      router.push(loginUrl)
    } catch (ex) {
      console.error(ex)
      alert(`Error verifying code: ${(ex as Error).message}`)
    }
  }

  const handleActivateCode = (result: VerifyCodeResult) => {
    dispatch({ type: 'SET_ACTIVATION_CODE', payload: result.code })
    dispatch({ type: 'SET_IS_CODE_ACTIVATED', payload: true })
  }

  return (
    <Page>
      <TopBar>
        <Link href="/">
          <GrandstandLogo width="200px" logoStyle="horiz" />
        </Link>
      </TopBar>
      <MultiStepProgressIndicator>
        <MultiStepItem stepNumber={1} text="Activate your device" state={isCodeActivated ? 'completed' : 'active'} />
        <MultiStepItem stepNumber={2} text="Connect TV Provider" state={isCodeActivated ? 'active' : 'inactive'} />
      </MultiStepProgressIndicator>
      {!isCodeActivated && (
        <FormContainer data-theme="light">
          <ActivateDevice onCodeActivated={handleActivateCode} />
        </FormContainer>
      )}
      {isCodeActivated && <MVPD onProviderSelected={handleProviderSelected} />}
    </Page>
  )
}
export default ActivatePage
